import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ArticleTemplate from '@/components/ArticleTemplate';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Sun, Mountain, Plane, Camera, Clock, Euro, Calendar, Star, Users, Thermometer, Umbrella } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";

const LomakohteetArticlePage = () => {
  const [currentImageUrl, setCurrentImageUrl] = useState("/lovable-uploads/5830f45f-5c08-4e8c-90f2-598da9e5d6f1.png");
  const isMobile = useIsMobile();
  
  const relatedArticles = [
    {
      id: "hauska-lomalaina",
      title: "Hauska lomalaina – Rahoita unelmalomasi Suomen 5 parhaaseen luontokohteeseen",
      slug: "hauska-lomalaina-rahoita-unelmalomasi-suomen-parhaisiin-luontokohteisiin",
      description: "Suunnitteletko kesälomaa? Katso 5 parasta suomalaista luontokohdetta ja miten rahoittaa lomasi järkevästi."
    },
    {
      id: "venelainaopas-2025",
      title: "Venelainaopas - Näin rahoitat unelmiesi veneen",
      slug: "venelainaopas-nain-rahoitat-unelmiesi-veneen",
      description: "Veneen hankintaan on tarjolla useita erilaisia rahoitusvaihtoehdot. Tämä opas auttaa sinua valitsemaan sopivimman ja edullisimman rahoitustavan."
    },
    {
      id: "caravan-matkailu-suomessa-2025",
      title: "Caravan-matkailu Suomessa 2025 - Kattava opas",
      slug: "caravan-matkailu-suomessa-2025",
      description: "Tutustu caravan-matkailun mahdollisuuksiin Suomessa. Parhaat kohteet, vinkit aloittelijoille ja rahoitusmahdollisuudet."
    }
  ];

  const articleContent = (
    <div className="article-content">
      <p>
        Matkailuala elää jännittäviä aikoja! Uudet trendit, kestävä matkailu ja ainutlaatuiset kokemukset määrittävät vuoden 2025 suosituimpia lomakohteita. Olemme koonneet kattavan oppaan, joka auttaa sinua löytämään täydellisen lomakohteen – olipa kyse kotimaan helmeistä tai eksoottisilta ulkomaan kohteista.
      </p>

      <div className="bg-blue-50 p-6 rounded-xl mb-8">
        <h2 id="matkailutrendit" className="text-2xl font-bold mb-4 flex items-center">
          <Star className="w-6 h-6 mr-2 text-yellow-500" />
          Vuoden 2025 matkailutrendit
        </h2>
        <ul className="space-y-3">
          <li className="flex items-start">
            <Sun className="w-5 h-5 text-green-600 mt-1 mr-2" />
            <span><strong>Kestävä matkailu:</strong> Ympäristöystävälliset kohteet ja vastuullinen matkustaminen</span>
          </li>
          <li className="flex items-start">
            <Users className="w-5 h-5 text-blue-600 mt-1 mr-2" />
            <span><strong>Slow travel:</strong> Pidempiaikaiset matkat ja syvällinen tutustuminen</span>
          </li>
          <li className="flex items-start">
            <Mountain className="w-5 h-5 text-green-600 mt-1 mr-2" />
            <span><strong>Luontomatkailu:</strong> Kansallispuistot ja luonnonsuojelualueet</span>
          </li>
          <li className="flex items-start">
            <Camera className="w-5 h-5 text-purple-600 mt-1 mr-2" />
            <span><strong>Digitox-matkat:</strong> Irtiotto teknologiasta ja rauhoittuminen</span>
          </li>
        </ul>
      </div>

      <h2 id="kotimaan-kohteet">Kotimaan suosikkikohteet 2025</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/86499215-bb3a-493d-887e-54fc5a8d53d3.png"
              alt="Lappi ja revontulet"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Mountain className="w-5 h-5 mr-2 text-blue-600" />
              Lappi - Revontulihaave
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Paras aika: Syys-maaliskuu
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                800-2000€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Lappi on edelleen ykköskohde revontulimatkailussa. Vuonna 2025 erityisesti Inari, Utsjoki ja Muonio tarjoavat upeita kokemuksia. Uudet lasikupolihotellit ja aurora-safareita tekevät kokemuksesta entistä erityisemmän.
            </p>
            <div className="space-y-2">
              <p><strong>Aktiviteetit:</strong> Revontulisafarit, koiravaljakkoajelut, lumikenkäkävelyt</p>
              <p><strong>Majoitus:</strong> Lasikupolit, tunturi-iglut, traditionaalisten hotellit</p>
              <p><strong>Erikoisuudet:</strong> Saamelaiskulttuuri, porofarmi-vierailut</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/766bbcbb-27d5-4f31-9774-09fc32b1bfe4.png"
              alt="Saimaan saaristo"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Sun className="w-5 h-5 mr-2 text-blue-600" />
              Saimaan saaristo
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Paras aika: Kesä-elokuu
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                400-1200€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Saimaan kristallinkirkas vesi ja tuhannet saaret tarjoavat rauhallisen pakopaikaan. Vuonna 2025 eko-veneilyt ja kestävä saaristomatkailu ovat entistä suositumpia.
            </p>
            <div className="space-y-2">
              <p><strong>Aktiviteetit:</strong> Veneilyä, kalastusta, melontaa, SUP-lautailua</p>
              <p><strong>Majoitus:</strong> Saaristomökit, boutique-hotellit, leirintäalueet</p>
              <p><strong>Erikoisuudet:</strong> Saimaannorppa, vanha kauppakeskus Lappeenranta</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/756226cb-7ced-47b0-ae6e-dbb52850c395.png"
              alt="Turun saaristo"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Plane className="w-5 h-5 mr-2 text-blue-600" />
              Turun saaristo
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Paras aika: Touko-syyskuu
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                300-900€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Turun saaristo tarjoaa helpon pääsyn saaristoloman tunnelmaan. Saaristobaana-reitti on maailman kaunein lauttareitti ja täydellinen tapa tutustua ainutlaatuiseen saaristokulttuuriin.
            </p>
            <div className="space-y-2">
              <p><strong>Aktiviteetit:</strong> Saaristobaana, pyöräily, kalastus, museokohteet</p>
              <p><strong>Majoitus:</strong> Perinteiset saaristotalot, B&B:t, camping</p>
              <p><strong>Erikoisuudet:</strong> Kultaranta, Hansan historia, käsityöläismylly</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/7ead0c3b-d7fe-4fdb-8426-6d0ffb3939a0.png"
              alt="Koli kansallispuisto"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Mountain className="w-5 h-5 mr-2 text-green-600" />
              Koli ja Karjala
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Paras aika: Ympäri vuoden
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                350-800€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Kolin kansallismaisema ja karjalainen kulttuuri yhdistyvät ainutlaatuiseksi kokemukseksi. Syksyn ruskamaisemista talven hiihtoreitteihin ja kesän vaelluksiin.
            </p>
            <div className="space-y-2">
              <p><strong>Aktiviteetit:</strong> Vaellus, hiihto, kultturitapahtumat, käsityöt</p>
              <p><strong>Majoitus:</strong> Mökkikylät, kansallispuiston majat, spa-hotellit</p>
              <p><strong>Erikoisuudet:</strong> Ukko-Koli, karjalainen kulttuuri, Pielisjoki</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <h2 id="ulkomaan-kohteet">Ulkomaan nousevat kohteet 2025</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/74a42011-2e94-4e1e-8924-91af3a378d8d.png"
              alt="Portugal ja Azurit"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Sun className="w-5 h-5 mr-2 text-orange-600" />
              Portugal ja Azurien saaret
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Thermometer className="w-4 h-4 mr-1" />
                18-25°C ympäri vuoden
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                600-1500€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Azurien saaret ovat nousseet suomalaistenkin suosioon. Nämä Atlantin helmet tarjoavat upeita kraattereita, kuumia lähteitä ja ainutlaatuista luontoa. Kestävä matkailu on saarilla prioriteetti.
            </p>
            <div className="space-y-2">
              <p><strong>Kohokohdat:</strong> São Miguel, Terceira, Faial-saaret</p>
              <p><strong>Aktiviteetit:</strong> Vaalaien tarkkailu, lämpimät lähteet, kraatterijärvet</p>
              <p><strong>Erikoisuudet:</strong> Endeminen kasvillisuus, maailmanluokan viinit</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/71b5bbd9-ace3-4241-8e61-6f74742c0663.png"
              alt="Slovenia ja järvet"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Mountain className="w-5 h-5 mr-2 text-green-600" />
              Slovenia - Alppimaiden helmi
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Thermometer className="w-4 h-4 mr-1" />
                15-28°C kesällä
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                400-1000€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Slovenia on Euroopan parhaiten säilynyt salaisuus. Bled-järvi ja Ljubljana tarjoavat täydellisen yhdistelmän kulttuuria ja luontoa. Maa on edelläkävijä ekomatkatailussa.
            </p>
            <div className="space-y-2">
              <p><strong>Kohokohdat:</strong> Bled-järvi, Škocjan-luolat, Ljubljana</p>
              <p><strong>Aktiviteetit:</strong> Vaellus, luolatutkimus, kulttuurikierros</p>
              <p><strong>Erikoisuudet:</strong> Satumainen Bled-linna, mehiläishoito-perinteet</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/6ec4385b-463a-4b93-a2b1-93211df83cb5.png"
              alt="Georgia ja vuoret"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Mountain className="w-5 h-5 mr-2 text-purple-600" />
              Georgia - Kaukasuksen aarre
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Thermometer className="w-4 h-4 mr-1" />
                20-30°C kesällä
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                300-800€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Georgia tarjoaa hämmästyttävää monimuotoisuutta: Kaukasuksen vuorista Mustanmeren rantoihin. Garstuvupa-kulttuuri ja legendaarinen viinitraditio tekevät kokemuksesta ainutlaatuisen.
            </p>
            <div className="space-y-2">
              <p><strong>Kohokohdat:</strong> Tbilisi, Svaneti-vuoret, Kahetian viinitilat</p>
              <p><strong>Aktiviteetit:</strong> Viinikierrot, vuorikiipeily, kulttuurikierros</p>
              <p><strong>Erikoisuudet:</strong> 8000 vuotta vanha viinitraditio, svanien tornitalot</p>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="relative h-48">
            <img
              src="/lovable-uploads/6b48ede2-c623-4f31-8efa-83f95aa56925.png"
              alt="Japani ja kirsikankukat"
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <Sun className="w-5 h-5 mr-2 text-pink-600" />
              Japani - Kirsikankukkien maa
            </h3>
            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Thermometer className="w-4 h-4 mr-1" />
                15-25°C keväällä
              </span>
              <span className="flex items-center">
                <Euro className="w-4 h-4 mr-1" />
                1200-2500€/hlö
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Japani on edelleen yksi halutuimmista matkakohteista. Vuonna 2025 kirsikankukka-sesong (sakura) ja syksyn lehväputoamiskausi tarjoavat unohtumattomia kokemuksia.
            </p>
            <div className="space-y-2">
              <p><strong>Kohokohdat:</strong> Tokio, Kyoto, Mount Fuji, Osaka</p>
              <p><strong>Aktiviteetit:</strong> Temppeli-kierrokset, sakura-katselu, kylpylät (onsen)</p>
              <p><strong>Erikoisuudet:</strong> Japanilainen kulttuuri, huippugastronomia, zen-puutarhat</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <h2 id="loman-rahoitus">Loman rahoittaminen järkevästi</h2>
      <p>
        Unelmaloma ei tarvitse jäädä haaveeksi budjetin takia. Monet suomalaiset rahoittavat lomansa kulutusluotolla tai pienlainalla, mikä mahdollistaa mukavan loman jakamisen helppoihin kuukausieriin.
      </p>

      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 my-8 rounded-xl border border-green-200 shadow-sm">
        <h3 className="text-xl font-bold mb-3 text-gray-900">Viisi vinkkiä loman rahoittamiseen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="bg-green-100 rounded-full p-1 mr-3 mt-1">
                <span className="text-green-700 font-bold text-sm">1</span>
              </div>
              <span className="text-gray-700"><strong>Vertaile lainatarjouksia</strong> - <Link to="/lainat/pienlainat-luottokortit" className="text-blue-600 hover:underline">pienlainat</Link> ja kulutusluotot tarjoavat joustavia vaihtoehtoja</span>
            </div>
            <div className="flex items-start">
              <div className="bg-green-100 rounded-full p-1 mr-3 mt-1">
                <span className="text-green-700 font-bold text-sm">2</span>
              </div>
              <span className="text-gray-700"><strong>Suunnittele ennakkoon</strong> - varaa loma-raha kuukausieriin jaettuna</span>
            </div>
            <div className="flex items-start">
              <div className="bg-green-100 rounded-full p-1 mr-3 mt-1">
                <span className="text-green-700 font-bold text-sm">3</span>
              </div>
              <span className="text-gray-700"><strong>Hyödynnä tarjouksia</strong> - matkatoimistojen maksuehdot voivat olla edullisia</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="bg-blue-100 rounded-full p-1 mr-3 mt-1">
                <span className="text-blue-700 font-bold text-sm">4</span>
              </div>
              <span className="text-gray-700"><strong>Tutki koron määrää</strong> - vertaile todellisia vuosikorkoja</span>
            </div>
            <div className="flex items-start">
              <div className="bg-blue-100 rounded-full p-1 mr-3 mt-1">
                <span className="text-blue-700 font-bold text-sm">5</span>
              </div>
              <span className="text-gray-700"><strong>Älä ylitä maksukykyäsi</strong> - loma-laina ei saa vaarantaa talouttasi</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4">
          Lainasampo.fi auttaa sinua löytämään sopivimman rahoitusvaihtoehdon unelmalomallesi. Vertailemme eri tarjoajien ehtoja ja autamme löytämään edullisimman vaihtoehdon.
        </p>
      </div>

      <h2 id="matkustusvakuutus">Matkustusvakuutus ja turvallisuus</h2>
      <p>
        Riippumatta siitä, valitsetko kotimaan vai ulkomaan kohteen, muista huolehtia asianmukaisesta matkustusvakuutuksesta. Monet lainanantajat tarjoavat myös matkavakuutuksia osana lainapakettejaan.
      </p>

      <div className="bg-yellow-50 p-6 rounded-xl mb-8 border border-yellow-200">
        <h3 className="text-lg font-bold mb-3 text-yellow-800 flex items-center">
          <Umbrella className="w-5 h-5 mr-2" />
          Turvallisuusvinkkejä 2025
        </h3>
        <ul className="space-y-2 text-gray-700">
          <li>• Tarkista kohdemaan matkustusvaroitukset ennen lähtöä</li>
          <li>• Hanki kattava matkustvakuutus, joka sisältää myös pandemiasuojan</li>
          <li>• Varaa joustavia perumisehtoja tarjoavia matkoja</li>
          <li>• Pidä tärkeät asiakirjat sähköisenä varmuuskopiona</li>
          <li>• Ilmoita pankeille ulkomaanmatkoista etukäteen</li>
        </ul>
      </div>

      <h2 id="kestava-matkailu">Kestävä matkailu vuonna 2025</h2>
      <p>
        Vuonna 2025 yhä useammat matkailijat valitsevat kestäviä matkustusvaihtoehtoja. Tämä tarkoittaa paikallisten yhteisöjen tukemista, ympäristöystävällisten kulkuvälineiden käyttöä ja vastuullisten majoittajien valitsemista.
      </p>
      <p>
        Kestävä matkailu ei ole vain ympäristöteko - se usein tarjoaa myös aidompia kokemuksia ja tukee paikallisia yhteisöjä. Monet kohteista tarjoavat nyt sertifioituja kestäviä matkailuvaihtoehtoja.
      </p>

      <div className="mt-8 mb-6">
        <LainaCta 
          text="Rahoita unelmalomasi nyt" 
          to="/lainat/pienlainat-luottokortit" 
          variant="primary" 
          size="lg" 
          className="w-full md:w-auto"
          trackingLabel="lomakohde-artikkeli-cta"
          location="lomakohteet-artikel"
        />
      </div>
    </div>
  );

  return (
    <ArticleTemplate
      title="Suosituimmat lomakohteet 2025 - Kotimaassa ja ulkomailla"
      description="Kokosimme yhteen tulevaisuuden suosituimmat lomakohteet vuodelle 2025. Tutustu niin kotimaan kauneimpiin kohteisiin kuin ulkomaan uusiin nouseviin matkailukohteisiin."
      date="2025-04-01"
      readTime="10 min"
      category="Kulutusluotot"
      imageUrl={currentImageUrl}
      relatedArticles={relatedArticles}
      content={articleContent}
    />
  );
};

export default LomakohteetArticlePage;